"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _material = require("@mui/material");
var _styles = require("@mui/styles");
var _system = require("@mui/system");
var _queryString = _interopRequireDefault(require("query-string"));
var _react = require("react");
var _reactRouterDom = require("react-router-dom");
var _Navigator = _interopRequireDefault(require("../App/Navigator.js"));
var _Flex = _interopRequireDefault(require("./Flex"));
var _Link = _interopRequireDefault(require("./Link"));
var _Select = _interopRequireDefault(require("./Select"));
var _String = _interopRequireDefault(require("./String"));
var _TabPanelHouseStyleModule = _interopRequireDefault(require("./TabPanelHouseStyle.module.scss"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const HopsTabs = (0, _styles.withStyles)(theme => ({
  indicator: _TabPanelHouseStyleModule.default.Indicator,
  root: {
    borderBottomColor: theme.palette.grey[400],
    borderBottomStyle: "solid",
    borderBottomWidth: "1px",
    paddingBottom: "0 !important",
    paddingTop: "0 !important"
  }
}))(_material.Tabs);
const HopsTab = (0, _styles.withStyles)(theme => ({
  root: {
    "textTransform": "none",
    "maxWidth": 250,
    "marginRight": theme.spacing(0.5),
    "marginBottom": theme.spacing(0.1),
    "color": theme.palette.grey[900],
    "backgroundColor": theme.palette.grey[300],
    "borderTopLeftRadius": theme.spacing(0.25),
    "borderTopRightRadius": theme.spacing(0.25),
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    },
    "&$selected": {
      color: theme.palette.primary.contrastText
    }
  },
  selected: {
    backgroundColor: theme.palette.primary.main
  },
  wrapper: {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap"
  }
}))(props => /*#__PURE__*/(0, _jsxRuntime.jsx)(_material.Tab, {
  disableRipple: true,
  ...props
}));

/**
 * @param {object[]} tabs 
 * @param {string} extraLinkLabel 
 * @param {string} extraLinkUri 
 * @param {boolean} sticky 
 * @param {boolean} hideWhenNoTabs 
 * @param {boolean} firstVisibleWhenNoAccess navigate to the first tab we have access to if we can't render the target tab
 * @returns {jsx}
 */
const TabPanelHouseStyle = ({
  tabs,
  extraLinkLabel,
  extraLinkUri,
  sticky,
  hideWhenNoTabs,
  firstVisibleWhenNoAccess,
  name,
  onChange,
  value
}) => {
  const {
    pathname,
    search
  } = (0, _reactRouterDom.useLocation)();
  const useSelect = (0, _system.useMediaQuery)("(max-width: 1024px)");

  /**
   * Tab changed.
   * 
   * @param {Event} e
   * @param {String} tabId
   * @return {void}
   */
  const handleChange = (e, tabId) => {
    if (tabId === extra_link_id) {
      _Navigator.default.navigate(extraLinkUri);
    }
    const tab = tabs.find(t => resolveTabId(t) === tabId);
    if (tab && onChange) {
      onChange(tabId, name);
      return;
    }
    if (tab) _Navigator.default.navigate(tab.uri || tab.uris?.[0]);
  };

  /**
   * Tab changed via `Select`.
   *
   * @param {String} tabId
   * @return {void}
   */
  const handleChangeSelect = tabId => handleChange(null, tabId);

  /**
   * Mounted.
   *
   * We dispatch a window resize event to ensure the 
   * active tab indicator is displayed correctly.
   *
   * https://github.com/mui/material-ui/issues/9337
   *
   * @return {void}
   */
  (0, _react.useEffect)(() => {
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent("resize"));
    }, 500);
  }, []);

  /**
   * Render our tabs.
   *
   * @param {Object|null} activeTab
   * @param {Array<Object>} tabs Tab definitions
   * @return {ReactNode}
   */
  const renderTabs = (activeTab, tabs) => /*#__PURE__*/(0, _jsxRuntime.jsxs)(HopsTabs, {
    classes: classes,
    indicatorColor: "primary",
    onChange: handleChange,
    textColor: "primary",
    TabScrollButtonProps: TabScrollButtonProps,
    style: style,
    value: resolveTabId(activeTab),
    variant: "scrollable",
    children: [tabs.map((tab, key) => /*#__PURE__*/(0, _jsxRuntime.jsx)(HopsTab, {
      component: _reactRouterDom.Link,
      className: _TabPanelHouseStyleModule.default.Tab,
      label: tab.label,
      style: tab.styles,
      value: resolveTabId(tab),
      to: tab.uri,
      onClick: e => e.preventDefault()
    }, key)), extraLinkUri && renderExtraLink()]
  });

  /**
   * Render as a selection dropdown.
   * 
   * @param {Object|null} activeTab
   * @param {Array<Object>} tabs Tab definitions
   * @return {ReactNode}
   */
  const renderSelect = (activeTab, tabs) => {
    const options = tabs.map(tab => {
      const id = resolveTabId(tab);
      return {
        ...tab,
        id,
        value: id
      };
    });
    if (extraLinkUri) {
      options.push({
        id: extra_link_id,
        label: extraLinkLabel,
        value: extra_link_id
      });
    }
    if (!activeTab) {
      options.push({
        id: -1,
        label: "(Invalid URI)",
        value: -1,
        disabled: true
      });
    }
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Select.default, {
      className: `${_TabPanelHouseStyleModule.default.Select} hideOnPrint`,
      onChange: handleChangeSelect,
      options: options,
      removeLabelOption: true,
      size: "small",
      style: style,
      value: resolveTabId(activeTab)
    });
  };

  /**
   * Render the "extra" link aside from the tabs.
   * 
   * @return {ReactNode}
   */
  const renderExtraLink = () => /*#__PURE__*/(0, _jsxRuntime.jsx)(_Link.default, {
    className: _TabPanelHouseStyleModule.default.ExtraLink,
    label: extraLinkLabel,
    uri: extraLinkUri
  });

  /**
   * Styles.
   * 
   * @return {Object}
   */
  const style = {
    minHeight: sticky ? "3rem" : undefined,
    paddingBottom: sticky ? "1rem" : undefined,
    paddingTop: sticky ? "0.5rem" : undefined,
    position: sticky ? "sticky" : undefined,
    top: sticky || undefined,
    zIndex: sticky ? 100 : undefined
  };

  /**
   * Get the active tab object.
   *
   * @return {Object|undefined}
   */
  const getTab = () => {
    const uri = `${pathname}${search}`;
    const qsRoute = _queryString.default.parse(search);

    /**
     * Uncontrolled value
     */
    if (value !== null) {
      return tabs.find((tab, i) => resolveTabId(tab, i) === value);
    }

    /**
     * Match our tab if given a specific tabParam
     */
    const tabParamMatching = tabs.find(t => t.param);
    if (tabParamMatching) {
      for (const tab of tabs) {
        const uris = getTabUris(tab);
        for (const u of uris) {
          // exact match
          if (u === uri) {
            return tab;
          }
        }

        // tab match
        if (tab.param) {
          for (const u of uris) {
            const [path, query] = u.split("?");
            if (path === pathname && query) {
              const qsTab = _queryString.default.parse(query);
              if (qsRoute[tab.param] === qsTab[tab.param]) {
                return tab;
              }
            }
          }
        }
      }
    }

    /**
     * Match our tab
     */
    return tabs.find(tab => {
      const uris = getTabUris(tab);

      /**
       * Iterate all our URIs
       */
      uriLoop: for (const u of uris) {
        /**
         * Not an exact match
         */
        if (u !== uri) {
          /**
           * Break the tab's URI
           */
          const [path, query] = u.split("?");

          /**
           * Pathame matches
           */
          if (path === pathname) {
            /**
             * Checking query string?
             */
            if (query) {
              /**
               * Parse the query string
               */
              const qsTab = _queryString.default.parse(query);

              /**
               * Query string keys
               */
              const qsKeys = Object.keys(qsTab);

              /**
               * All query params need to match values
               */
              for (const param of qsKeys) {
                if (qsRoute[param] !== qsTab[param]) {
                  continue uriLoop;
                }
              }
              return true;
            } else {
              return true;
            }
          }
        } else {
          return true;
        }
      }

      /**
       * Using regex matches
       */
      if (tab.uriMatches) {
        return !!tab.uriMatches.find(u => {
          return new RegExp(u).test(uri);
        });
      }

      /**
       * Nothing matches
       */
      return false;
    });
  };

  /**
   * Get visible tabs to show.
   * 
   * @return {Array}
   */
  const getTabsVisible = () => tabs.filter(tab => !tab.hidden);

  /**
   * Render.
   * 
   * @return {ReactNode}
   */
  const visibleTabs = sortTabs(getTabsVisible());
  if (!visibleTabs.length && hideWhenNoTabs) return null;
  let tab = getTab();
  const hide404 = tab?.hide404;
  if (tab?.hidden) tab = null;
  let component = null;
  if (!tab) {
    component = hide404 ? null : render404();
  } else if (!tab.permission) {
    component = tab.render?.();
  } else if (firstVisibleWhenNoAccess && visibleTabs.length) {
    _Navigator.default.navigate(visibleTabs[0].uri || visibleTabs[0].uris?.[0]);
    return null;
  }
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
    children: [useSelect ? renderSelect(tab, visibleTabs) : renderTabs(tab, visibleTabs), component]
  });
};

/**
 * Tab URIs can be defined as an array or single element
 */
const getTabUris = tab => tab.uris || tab.uri && [tab.uri] || [];

/**
 * Resolve a tab's ID.
 *
 * We use the tab label as a fallback when no ID's assigned.
 *
 * @param {Object} tab
 * @return {String|null}
 */
const resolveTabId = tab => {
  return tab?.id || tab?.label;
};

/**
 * Sort tab definition objects.
 *
 * @param {Array<Object>} tabs
 * @return {Array<Object>}
 */
const sortTabs = tabs => {
  return tabs.sort((a, b) => {
    if (a.order !== undefined && b.order === undefined) return -1;else if (b.order !== undefined && a.order === undefined) return 1;else if (a.order < b.order) return -1;else if (a.order > b.order) return 1;else return 0;
  });
};

/**
 * Render 404 screen.
 * 
 * @return {ReactNode}
 */
const render404 = () => /*#__PURE__*/(0, _jsxRuntime.jsxs)(_Flex.default, {
  children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
    str: "Not Found",
    variant: "h6"
  }), /*#__PURE__*/(0, _jsxRuntime.jsx)(_String.default, {
    color: "textSecondary",
    str: "Please check the URL you've visited."
  })]
});

/**
 * Classes.
 * 
 * @type {Object}
 */
const classes = {
  indicator: _TabPanelHouseStyleModule.default.Indicator,
  root: _TabPanelHouseStyleModule.default.Tabs
};

/**
 * `TabScrollButtonProps`
 * 
 * @type {Object}
 */
const TabScrollButtonProps = {
  disabled: false
};

/**
 * ID of our "extra link" when it's rendered as an option.
 *
 * @type {String}
 */
const extra_link_id = "extra";
var _default = exports.default = TabPanelHouseStyle;